import { ConfigDatabase } from '../../engine/database';
import { asteroidsData, type AsteroidConfig } from '../../generated/asteroids';

export type AsteroidKey = string;

/**
 * База данных для конфигураций астероидов
 * Объединяет кодоген из гугл-таблиц с локальными overrides
 */
export class AsteroidsDB extends ConfigDatabase<AsteroidConfig, string> {
  constructor() {
    super({
      base: asteroidsData,
      overrides: {}, // Пока без локальных правок
      getId: config => config.asteroid_id,
      validate: (config, id) => {
        if (config.base_hp_unit <= 0) {
          throw new Error(`Asteroid ${id}: base_hp_unit must be > 0`);
        }
        if (config.tier < 1) {
          throw new Error(`Asteroid ${id}: tier must be >= 1`);
        }
      },
    });
  }

  /**
   * Инициализация базы данных
   */
  init(): void {
    // ConfigDatabase уже имеет init() метод, просто вызываем его
    super.init();
  }

  /**
   * Получить конфигурацию астероида по ID
   */
  getAsteroidConfig(id: AsteroidKey): AsteroidConfig | undefined {
    return this.get(id);
  }

  /**
   * Получить все астероиды определенного тира
   */
  getAsteroidsByTier(tier: number): AsteroidConfig[] {
    return this.all().filter(asteroid => asteroid.tier === tier);
  }

  /**
   * Получить все доступные ID астероидов
   */
  getAsteroidIds(): AsteroidKey[] {
    return this.ids() as AsteroidKey[];
  }

  /**
   * Получить максимальный тир
   */
  getMaxTier(): number {
    return Math.max(...this.all().map(a => a.tier), 1);
  }

  /**
   * Получить астероиды для спауна по весам (для будущего расширения)
   */
  getWeightedAsteroids(): Array<{ config: AsteroidConfig; weight: number }> {
    // Пока просто возвращаем все с равными весами
    // В будущем можно добавить веса из конфига
    return this.all().map(config => ({
      config,
      weight: 1.0 / config.tier, // Чем выше тир, тем реже спаун
    }));
  }
}

// Ленивый синглтон для удобства
let _asteroidsInstance: AsteroidsDB | null = null;
export function getAsteroids(): AsteroidsDB {
  if (!_asteroidsInstance) {
    _asteroidsInstance = new AsteroidsDB();
  }
  return _asteroidsInstance;
}

export const Asteroids = new Proxy({} as AsteroidsDB, {
  get(_target, prop) {
    const instance = getAsteroids();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const method = instance[prop];

    if (typeof method === 'function') {
      return method.bind(instance);
    }
    return method;
  },
});

// Реэкспортируем типы для удобства
export type { AsteroidConfig };
